"use client";

import { useState } from "react";
import { Search, MapPin, Navigation, ChevronDown } from "lucide-react";
import { TradePicker } from "@/app/components/TradePicker";
import { isValidPostcodeFormat, formatPostcode } from "@/lib/postcode";

interface BrowseSearchBarProps {
  defaultPostcode?: string;
  defaultTrade?: string;
  defaultRadius?: string;
  defaultName?: string;
  currentFilters?: {
    minRating?: string;
    availability?: string;
    qualified?: string;
    sort?: string;
    map?: string;
  };
}

export function BrowseSearchBar({
  defaultPostcode,
  defaultTrade,
  defaultRadius,
  defaultName,
  currentFilters = {},
}: BrowseSearchBarProps) {
  const [trade, setTrade] = useState(defaultTrade ?? "");
  const [postcode, setPostcode] = useState(defaultPostcode ?? "");
  const [postcodeError, setPostcodeError] = useState("");

  function handlePostcodeBlur() {
    const trimmed = postcode.trim();
    if (trimmed && isValidPostcodeFormat(trimmed)) {
      setPostcode(formatPostcode(trimmed));
      setPostcodeError("");
    } else if (trimmed) {
      setPostcodeError("Please enter a valid UK postcode");
    } else {
      setPostcodeError("");
    }
  }

  return (
    <section className="bg-cream border-b border-stone/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
        <form action="/browse" className="flex flex-col lg:flex-row gap-4">
          {/* Preserve current filter params as hidden inputs */}
          {currentFilters.minRating && (
            <input type="hidden" name="minRating" value={currentFilters.minRating} />
          )}
          {currentFilters.availability && (
            <input type="hidden" name="availability" value={currentFilters.availability} />
          )}
          {currentFilters.qualified && (
            <input type="hidden" name="qualified" value={currentFilters.qualified} />
          )}
          {currentFilters.sort && (
            <input type="hidden" name="sort" value={currentFilters.sort} />
          )}
          {currentFilters.map && (
            <input type="hidden" name="map" value={currentFilters.map} />
          )}
          {defaultName && (
            <input type="hidden" name="name" value={defaultName} />
          )}

          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Trade category */}
            <div className="relative group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none z-10 group-focus-within:text-forest transition-colors" />
              <TradePicker
                value={trade}
                onChange={setTrade}
                name="trade"
                placeholder="Trade category"
                ariaLabel="Trade category"
                className="w-full pl-12 pr-4 py-3 bg-white rounded-xl font-medium"
              />
            </div>
            {/* Postcode */}
            <div className="relative group">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted group-focus-within:text-forest transition-colors z-10" />
              <input
                name="postcode"
                type="text"
                value={postcode}
                onChange={(e) => {
                  setPostcode(e.target.value);
                  if (postcodeError && isValidPostcodeFormat(e.target.value.trim())) {
                    setPostcodeError("");
                  }
                }}
                onBlur={handlePostcodeBlur}
                placeholder="Postcode"
                aria-label="Postcode"
                autoComplete="postal-code"
                className={`w-full pl-12 pr-4 py-3 bg-white rounded-xl text-charcoal font-medium focus:ring-2 focus:ring-forest/20 outline-none ${
                  postcodeError ? "ring-2 ring-error" : "border-none"
                }`}
              />
              {postcodeError && (
                <p className="absolute text-xs text-error mt-1 left-0">{postcodeError}</p>
              )}
            </div>
            {/* Distance */}
            <div className="relative group">
              <Navigation className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted group-focus-within:text-forest transition-colors" />
              <select
                name="radius"
                defaultValue={defaultRadius || "25"}
                aria-label="Search radius"
                className="w-full pl-12 pr-10 py-3 bg-white border-none rounded-xl text-charcoal font-medium focus:ring-2 focus:ring-forest/20 outline-none appearance-none cursor-pointer"
              >
                <option value="5">5 miles</option>
                <option value="10">10 miles</option>
                <option value="15">15 miles</option>
                <option value="25">25 miles</option>
                <option value="50">50 miles</option>
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
            </div>
          </div>

          <button
            type="submit"
            className="bg-forest text-white px-10 py-3 rounded-xl font-serif font-bold hover:opacity-90 transition-all shadow-lg shadow-forest/10 cursor-pointer border-none"
          >
            Search
          </button>
        </form>
      </div>
    </section>
  );
}
